import type Konva from "konva";
import { Line, Rect } from "react-konva";

import { flatPoints, traceShape, tracePolyline } from "../../editor/geometry";
import type { EditableRegion, Point } from "../../editor/types";

export const SELECTION_COLOR = "#ff654f";
const HALO_COLOR = "rgba(255, 255, 255, 0.92)";
const IDLE_COLOR = "rgba(16, 24, 24, 0.78)";

export type OutlineState = "hidden" | "idle" | "hover" | "selected";

type KonvaContext = Konva.Context;

/** Screen pixels → the shape's local units, so outlines stay hairline at any zoom. */
function px(shape: Konva.Shape): number {
  return 1 / Math.max(0.0001, shape.getAbsoluteScale().x);
}

function pointsOf(shape: Konva.Shape): Point[] {
  const flat = (shape as Konva.Line).points();
  const points: Point[] = [];
  for (let index = 0; index < flat.length; index += 2) {
    points.push({ x: flat[index], y: flat[index + 1] });
  }
  return points;
}

/** Two-tone line that reads on both light and dark photos. */
function strokeTwoTone(context: KonvaContext, unit: number, state: OutlineState): void {
  context.setLineDash([]);
  context.setAttr("lineWidth", 3.5 * unit);
  context.setAttr("strokeStyle", HALO_COLOR);
  context.stroke();
  context.setAttr("lineWidth", (state === "idle" ? 1.25 : 2) * unit);
  context.setAttr("strokeStyle", state === "idle" ? IDLE_COLOR : SELECTION_COLOR);
  context.setLineDash(state === "idle" ? [5 * unit, 4 * unit] : []);
  context.stroke();
  context.setLineDash([]);
}

/**
 * Outline of a thick brush path: paint the path slightly wider, then cut the
 * brush itself out. Only the rim remains, so the mosaic underneath stays
 * visible instead of being tinted by an overlay. Needs its own layer because
 * the cut also removes whatever this layer drew underneath.
 */
function strokeBrushRim(
  context: KonvaContext,
  points: Point[],
  width: number,
  unit: number,
  state: OutlineState,
): void {
  context.setAttr("lineCap", "round");
  context.setAttr("lineJoin", "round");
  tracePolyline(context, points, false);
  context.setAttr("lineWidth", width + 4 * unit);
  context.setAttr("strokeStyle", HALO_COLOR);
  context.stroke();
  context.setAttr("lineWidth", width + 2 * (state === "idle" ? 1.25 : 2) * unit);
  context.setAttr("strokeStyle", state === "idle" ? IDLE_COLOR : SELECTION_COLOR);
  context.stroke();
  context.setAttr("globalCompositeOperation", "destination-out");
  context.setAttr("lineWidth", width);
  context.setAttr("strokeStyle", "#000");
  context.stroke();
  context.setAttr("globalCompositeOperation", "source-over");
}

interface RegionNodeProps {
  region: EditableRegion;
  outline: OutlineState;
  listening: boolean;
  draggable: boolean;
  /** Minimum hit width for brush strokes, in image units. */
  minHitWidth: number;
  onPointerDown?: (event: Konva.KonvaEventObject<PointerEvent>) => void;
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onDragStart?: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onDragMove?: (event: Konva.KonvaEventObject<DragEvent>) => void;
  onDragEnd?: (event: Konva.KonvaEventObject<DragEvent>) => void;
}

/**
 * One editable range. The node carries the real geometry, so the transformer
 * and hit testing work on it, while the scene function only draws an outline:
 * the effect itself comes from the rendered image below.
 */
export function RegionNode({
  region,
  outline,
  listening,
  draggable,
  minHitWidth,
  ...handlers
}: RegionNodeProps) {
  const common = {
    id: `region-${region.id}`,
    name: "region",
    x: region.x,
    y: region.y,
    rotation: region.rotation,
    listening,
    draggable,
    ...handlers,
  };

  if (region.kind === "stroke") {
    return (
      <Line
        {...common}
        points={flatPoints(region.points)}
        stroke="#000"
        strokeWidth={region.penWidth}
        hitStrokeWidth={Math.max(region.penWidth, minHitWidth)}
        lineCap="round"
        lineJoin="round"
        perfectDrawEnabled={false}
        sceneFunc={(context, shape) => {
          if (outline === "hidden") return;
          strokeBrushRim(context, pointsOf(shape), shape.strokeWidth(), px(shape), outline);
        }}
        hitFunc={(context, shape) => {
          tracePolyline(context, pointsOf(shape), false);
          context.strokeShape(shape);
        }}
      />
    );
  }

  if (region.kind === "polygon") {
    return (
      <Line
        {...common}
        points={flatPoints(region.points)}
        closed
        fill="#000"
        strokeEnabled={false}
        perfectDrawEnabled={false}
        sceneFunc={(context, shape) => {
          if (outline === "hidden") return;
          tracePolyline(context, pointsOf(shape), true);
          strokeTwoTone(context, px(shape), outline);
        }}
        hitFunc={(context, shape) => {
          tracePolyline(context, pointsOf(shape), true);
          context.fillShape(shape);
        }}
      />
    );
  }

  return (
    <Rect
      {...common}
      width={region.width}
      height={region.height}
      fill="#000"
      strokeEnabled={false}
      perfectDrawEnabled={false}
      sceneFunc={(context, shape) => {
        if (outline === "hidden") return;
        traceShape(context, region.kind, shape.width(), shape.height());
        strokeTwoTone(context, px(shape), outline);
      }}
      hitFunc={(context, shape) => {
        traceShape(context, region.kind, shape.width(), shape.height());
        context.fillShape(shape);
      }}
    />
  );
}
